import { useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { LoadingScreen } from "./LoadingScreen";

/**
 * Route handler for /auth-transfer.
 * Reads tokens from the URL hash fragment (as _at / _rt to avoid
 * collisions with Supabase's detectSessionInUrl),
 * sets the Supabase session, then does a full page reload to /.
 *
 * A full reload (not client-side navigate) is required so that
 * AuthProvider remounts, calls getSession(), and fetches the Rails user.
 *
 * The hash is cleared immediately via replaceState for security —
 * hash fragments are never sent to the server, but clearing them
 * prevents accidental exposure in browser history.
 */
export function AuthTransferPage() {
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    async function transfer() {
      const hash = window.location.hash.substring(1); // remove leading #
      const params = new URLSearchParams(hash);
      const accessToken = params.get("_at");
      const refreshToken = params.get("_rt");

      // Clear the hash immediately
      window.history.replaceState(null, "", window.location.pathname);

      if (!accessToken || !refreshToken) {
        window.location.replace("/login");
        return;
      }

      try {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (error) {
          window.location.replace("/login");
          return;
        }

        // Session set successfully — full reload so AuthProvider remounts
        // and picks up the persisted session via getSession().
        window.location.replace("/");
      } catch {
        window.location.replace("/login");
      }
    }

    transfer();
  }, []);

  return <LoadingScreen />;
}
